import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import type { SearchPreferences, SearchPreferencesUpdate } from "@searchui-searchforms/shared/user-preferences";
import { usePageContextQuery } from "@/shared/page-context";
import { getUserPreferences } from "./getUserPreferences";
import { mergePayloadIntoPreferences } from "./mergePayloadIntoPreferences";
import { saveSearchPreferences } from "./saveSearchPreferences";

export const userPreferencesQueryKey = ["user-preferences"];

export const userPreferencesQueryOptions = () =>
    queryOptions({
        queryKey: userPreferencesQueryKey,
        queryFn: () => getUserPreferences().catch(() => null),
    });

export const useUserPreferencesQuery = () => {
    return useSuspenseQuery(userPreferencesQueryOptions());
};

export const useSaveUserPreferencesMutation = () => {
    const { data: pageContext } = usePageContextQuery();
    const queryClient = useQueryClient();

    // Preferences require a personal authenticated session; the server remains the authorization boundary.
    const canSavePreferences = pageContext.isAuthenticated && !pageContext.isInstitutional;

    return useMutation({
        mutationFn: (data: SearchPreferencesUpdate) => {
            if (!canSavePreferences) return Promise.resolve();
            return saveSearchPreferences(data);
        },
        onMutate: (data) => {
            if (!canSavePreferences) return;

            // Optimistic update: merge submitted preferences into the cache so that
            // bfcache-restored pages see the freshly-saved values (invalidation is
            // unreliable after bfcache restore since no refetch is triggered).
            const cached = queryClient.getQueryData<SearchPreferences | null>(userPreferencesQueryKey);

            // When cached value is null (institutional user or failed initial fetch),
            // skip the optimistic write — there's no baseline to merge into.
            if (cached == null) return;

            queryClient.setQueryData(userPreferencesQueryKey, mergePayloadIntoPreferences(cached, data));
        },
    });
};
